/* eslint-disable no-nested-ternary */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DocumentPreview from '../shipments/stops/DocumentPreview';
import { useGetDocuments } from '../../../utils/DocumentsHooks';
import { PROOF_OF_DELIVERY, PROOF_OF_PICKUP } from '../../../config/documents';
import { getSignedUrl } from '../../../utils/DocumentsAPIUtils';
import { FailedDocument } from './FailedDocument';
import { DOCUMENT_STATUSES } from './Document';
import LoadingCircle from '../../ui/LoadingCircle';
import { showToast } from '../../../stores/ToastStore';
import { FETCH_POLICY_NETWORK_ONLY } from '../../../constants/graphql-constants';

export function PodDocumentsList({ shipment, stop, onInit, refetchRef }) {
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const refetchIntervalRef = useRef(0);
  const { t } = useTranslation();

  const {
    data: documentsData,
    error: documentError,
    refetch: documentsRefetch,
  } = useGetDocuments({
    foreignKeys: [stop ? stop.id : shipment.id].filter((key) => key),
    type:
      stop?.action !== 'deliver'
        ? PROOF_OF_PICKUP.value
        : PROOF_OF_DELIVERY.value,
    fetchPolicy: FETCH_POLICY_NETWORK_ONLY,
  });

  const initDocuments = async () => {
    if (documentError) {
      showToast(
        t('GET_DOCUMENT_ERROR'),
        t('GET_DOCUMENT_FAILED_WITH_ERROR', { error: documentError }),
        'error',
      );
    }

    if (!documentsData) {
      return;
    }

    setLoading(true);

    const hasPendingDocuments = !!documentsData?.documents?.find(
      (document) => document.status === DOCUMENT_STATUSES.PENDING,
    );
    const hasFailedDocuments = !!documentsData?.documents?.find(
      (document) => document.status === DOCUMENT_STATUSES.UPLOAD_FAILED,
    );

    if (hasPendingDocuments || hasFailedDocuments) {
      clearTimeout(refetchIntervalRef.current);

      refetchIntervalRef.current = setInterval(() => {
        refetchIntervalRef.current = 0;
        documentsRefetch();
      }, 3000);
    }

    const documents = [];

    await Promise.all(
      documentsData?.documents.map(async (document) => {
        let signedUrl;

        if (document.status === DOCUMENT_STATUSES.UPLOADED) {
          const { data, errors: signedUrlErrors } = await getSignedUrl(
            document.key,
          );
          if (signedUrlErrors) {
            showToast(
              t('GET_SIGNED_URL_ERROR'),
              t('GET_SIGNED_URL_FAILED_WITH_ERROR', {
                error: signedUrlErrors[0].message,
              }),
              'error',
            );
            return;
          }
          signedUrl = data?.getSignedUrl?.signedUrl;
        }

        documents.push({
          ...document,
          signedUrl,
        });
      }),
    );

    setDocuments(documents);
    setLoading(false);

    onInit?.(documents);
  };

  useEffect(() => {
    refetchRef?.(documentsRefetch);

    return () => {
      refetchRef?.(null);
    };
  }, [documentsRefetch]);

  useEffect(() => {
    initDocuments();

    return () => {
      clearTimeout(refetchIntervalRef.current);
    };
  }, [documentsData, documentError]);

  return (
    <div className="grid-3-x-3">
      {loading && <LoadingCircle text={t('PROCESSING_FILES')} />}
      {!loading &&
        documents.map((document, idx) => (
          <div key={idx} className="px-4 py-2 m-auto">
            {document.status === DOCUMENT_STATUSES.UPLOAD_FAILED ? (
              <FailedDocument
                document={document}
                onDelete={() => documentsRefetch()}
              />
            ) : document.status === DOCUMENT_STATUSES.PENDING ? (
              <LoadingCircle text="Loading..." />
            ) : (
              <DocumentPreview
                url={document.signedUrl}
                displayInGridView
                renderDownloadableImage
              />
            )}
          </div>
        ))}
    </div>
  );
}
